/**
 * Modern skin design tokens.
 *
 * Ported verbatim from the design prototype's `src/index.css` `:root` block and
 * its `html[data-theme="dark"]` override. These are delivered to components
 * on the MUI theme as `theme.modern`, reached by typing the style callback as
 * `ModernTheme` (below) — so a `styles.ts` writes each token exactly once and
 * gets the correct light/dark value automatically when the Modern
 * ThemeProvider swaps themes.
 *
 * Nothing outside `src/theme/modern/` and the `modern/` component folders may
 * read these — the Classic skin keeps its own untouched theme in ../index.ts,
 * and the `ModernTheme` type below is what enforces that at compile time.
 */

import {Theme} from "@material-ui/core/styles/createMuiTheme"

export type ModernTokens = {
    // surfaces
    bg: string
    panel: string
    panel2: string
    line: string
    line2: string
    // text
    ink: string
    ink2: string
    ink3: string
    // brand
    accent: string
    accentSoft: string
    accentLine: string
    // status
    ok: string
    okSoft: string
    warn: string
    warnSoft: string
    crit: string
    critSoft: string
    /**
     * Text/icon colour to use ON an accent, ok, warn or crit FILL.
     *
     * Not always white: in reading-room mode the status colours lighten
     * (accent #5B93FF, ok #3DCB92), and white on those is ~1.9:1 — well under
     * the 4.5:1 floor. Dark ink on the light fill is the readable pairing.
     *
     * The LIGHT palette has to clear the same floor, which it originally did
     * not: white on the old `ok` #0E8A5F was 4.36:1 and on `warn` #A8730A was
     * 4.10:1 — and `ok` is the fill behind the primary Approve button, in the
     * skin and mode that are now the default. Both were darkened; every
     * onAccent pairing in both modes is now >= 4.5:1.
     */
    onAccent: string
    // ECG surface
    paper: string
    gridMinor: string
    gridMajor: string
    trace: string
    beat: string
    caliper: string
    // precomputed translucents — never emit color-mix(), CRA 3.3's browser
    // matrix predates it and this app ships to embedded Safari
    resOkBorder: string
    resWarnBorder: string
    hintBg: string
    overlayBg: string
    // elevation
    shadow: string
    shadowHover: string
    shadowSegment: string
    // typography / geometry
    sans: string
    mono: string
    radius: number
    appBarHeight: number
    toolbarHeight: number
    keysBarHeight: number
}

/**
 * The MUI theme as seen from INSIDE ModernThemeProvider, where `modern` is
 * guaranteed present.
 *
 * Modern style sheets annotate their callback with this type
 * (`makeStyles((theme: ModernTheme) => ...)`) instead of the tokens being
 * declared on the global `Theme`. Declaring them globally made
 * `theme.modern.bg` compile in CLASSIC style files too, where the value is
 * undefined at runtime — a white screen for every Classic user, with the
 * compiler offering no warning and only a hand-written comment in
 * UiSkinToggle/styles.ts standing in for type safety.
 */
export type ModernTheme = Theme & {modern: ModernTokens}

const SANS = "'Alexer',-apple-system,'Segoe UI',sans-serif";
const MONO = SANS;

export const MODERN_LIGHT: ModernTokens = {
    bg: "#EFF3F8",
    panel: "#FFFFFF",
    panel2: "#F7FAFD",
    line: "#DBE3ED",
    line2: "#EAEFF5",
    ink: "#16233A",
    ink2: "#56677F",
    ink3: "#8C99AC",
    accent: "#1E5EF3",
    accentSoft: "#E8EFFE",
    accentLine: "#B9CDFB",
    // white-on-fill 4.94:1 (was #0E8A5F, 4.36:1 — below the AA floor)
    ok: "#0D8058",
    okSoft: "#E2F4EC",
    // white-on-fill 4.73:1 (was #A8730A, 4.10:1)
    warn: "#9A6A09",
    warnSoft: "#FBF1DC",
    crit: "#C2382C",
    critSoft: "#FBE8E6",
    onAccent: "#FFFFFF",
    paper: "#FFFFFF",
    gridMinor: "#F6D8DA",
    gridMajor: "#F48284",
    trace: "#555555",
    beat: "#0000FF",
    caliper: "#1E5EF3",
    resOkBorder: "rgba(14,138,95,.30)",
    resWarnBorder: "rgba(168,115,10,.30)",
    hintBg: "rgba(255,255,255,.92)",
    overlayBg: "rgba(22,35,58,.28)",
    shadow: "0 1px 2px rgba(22,35,58,.06),0 4px 14px rgba(22,35,58,.05)",
    shadowHover: "0 5px 16px rgba(22,35,58,.12)",
    shadowSegment: "0 1px 2px rgba(22,35,58,.12)",
    sans: SANS,
    mono: MONO,
    radius: 10,
    appBarHeight: 52,
    toolbarHeight: 42,
    keysBarHeight: 42,
};

export const MODERN_DARK: ModernTokens = {
    ...MODERN_LIGHT,
    bg: "#0A1019",
    panel: "#111927",
    panel2: "#0D1521",
    line: "#243349",
    line2: "#1B2738",
    ink: "#DCE6F5",
    ink2: "#94A6C0",
    ink3: "#5F7191",
    accent: "#5B93FF",
    accentSoft: "#16243F",
    accentLine: "#2B477C",
    ok: "#3DCB92",
    okSoft: "#0F2B21",
    warn: "#E3A93C",
    warnSoft: "#2E2410",
    crit: "#F0655A",
    critSoft: "#371714",
    // dark ink on the lightened dark-mode fills (white would be ~1.9:1)
    onAccent: "#0A1019",
    paper: "#0C141F",
    gridMinor: "#1B2839",
    gridMajor: "#2A3D58",
    trace: "#46E0A1",
    beat: "#5B93FF",
    caliper: "#5B93FF",
    resOkBorder: "rgba(61,203,146,.30)",
    resWarnBorder: "rgba(227,169,60,.30)",
    hintBg: "rgba(17,25,39,.92)",
    overlayBg: "rgba(0,0,0,.5)",
    shadow: "0 1px 2px rgba(0,0,0,.4),0 6px 18px rgba(0,0,0,.35)",
    // deliberate fix, not a bug-for-bug port: the prototype's hover/segment
    // shadows are hard-coded light-only and vanish on a dark panel
    shadowHover: "0 5px 16px rgba(0,0,0,.5)",
    shadowSegment: "0 1px 2px rgba(0,0,0,.5)",
};

export const MODERN_TOKENS: Record<"light" | "dark", ModernTokens> = {
    light: MODERN_LIGHT,
    dark: MODERN_DARK,
};
